// «HE OLVIDADO MI CONTRASEÑA» — las dos pantallas públicas, sin sesión.
//
//   /olvide-mi-contrasena   pedir el enlace
//   /restablecer/:token     canjearlo: código + 4 últimos del móvil + contraseña nueva
//
// EL ENLACE SOLO NO BASTA, y la pantalla lo explica sin asustar: hace falta el código (que llega
// por WhatsApp si se pudo, y si no en el mismo correo) y los 4 últimos dígitos del teléfono de la
// ficha. El servidor dice qué pedir (`GET /cuenta/restablecer/:token`), así que esta pantalla no
// decide nada de seguridad: solo pinta lo que le mandan.
//
// Va con `axios` a pelo y no con `services/api`: ese cliente mete el token de sesión y redirige al
// login cuando hay 401, y aquí no hay sesión ninguna. Mismo criterio que InvitacionAceptar.jsx.
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import axios from 'axios'
import { InputPassword, Boton, Ico } from '../components/ui'
import { t, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Los colores de BIANGO, no `--primary`: en las pantallas públicas no manda ninguna clínica.
// `boton` y `enlace` (5-oct-2026): el principal y el acento de Biango para la interfaz (#1B2130, 16:1 con letra blanca;
// #465066, 8:1 sobre blanco), los que eligió el dueño para lo que no es de ninguna clínica (index.css · --biango-principal,
// --biango-secundario). El azul (#1E88E5) es el del logotipo.
const B = { azul: '#1E88E5', boton: '#1B2130', enlace: '#465066', tinta: '#22252B', niebla: '#F1F3F6', gris: '#8C96A3' }
const API = '/api/cuenta'

const marco = {
  minHeight: 'var(--alto-pantalla)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '24px 16px', background: B.niebla,
}
const tarjeta = {
  width: '100%', maxWidth: '420px', background: '#fff', borderRadius: '18px',
  padding: 'clamp(24px, (5vw / var(--z, 1)), 36px)', boxShadow: '0 24px 60px rgba(34,37,43,.10)',
}
const label = { display: 'block', fontSize: '12px', fontWeight: 800, letterSpacing: '.3px', color: B.tinta, marginBottom: '6px' }
const input = {
  width: '100%', padding: '12px 14px', border: '1.5px solid #E3E7ED', borderRadius: '12px',
  fontSize: '15px', boxSizing: 'border-box', background: '#fff', color: B.tinta,
}
const ayuda = { fontSize: '12.5px', color: B.gris, lineHeight: 1.5, marginTop: '6px' }
const boton = {
  width: '100%', justifyContent: 'center', background: B.boton,
  border: 'none', color: '#fff', fontWeight: 800, borderRadius: '12px', padding: '13px', fontSize: '14px',
  boxShadow: '0 8px 22px rgba(27,33,48,.28)',
}

function Aviso({ tipo, children }) {
  const malo = tipo === 'error'
  return (
    <div style={{
      display: 'flex', gap: '9px', alignItems: 'flex-start', margin: '0 0 16px',
      background: malo ? 'var(--danger-bg, #FEE2E2)' : 'var(--success-bg, #DCFCE7)',
      color: malo ? '#991B1B' : '#14532D',
      border: '1px solid ' + (malo ? '#FCA5A5' : '#86EFAC'),
      borderRadius: '12px', padding: '11px 13px', fontSize: '13px', lineHeight: 1.5,
    }}>
      <span style={{ flexShrink: 0, marginTop: '1px' }}><Ico n={malo ? 'info' : 'check'} s={15} /></span>
      <span>{children}</span>
    </div>
  )
}

function Cabecera({ titulo, bajada }) {
  return (
    <>
      {/* LA MARCA, DE LA PROPIA APLICACIÓN. Antes apuntaba a `/estatico/img/marca/…`, que es una dirección del SITIO WEB
          (biango.es, carpeta web/): en la aplicación no existe, así que aquí no se veía el logo — el mismo fallo que dejó
          el chat sin fondo. `biango-marca.svg` viaja en frontend/public, o sea en todos los despliegues. */}
      <img src="/biango-marca.svg" alt={t('pages.RestablecerPassword.biango', 'Biango')} width="112"
        style={{ display: 'block', width: '112px', height: 'auto', marginBottom: '22px' }}
        onError={e => { e.currentTarget.style.display = 'none' }} />
      <h1 style={{ fontSize: '22px', fontWeight: 800, color: B.tinta, margin: '0 0 8px', letterSpacing: '-.02em' }}>{titulo}</h1>
      {bajada && <p style={{ fontSize: '14px', color: '#4A5563', lineHeight: 1.55, margin: '0 0 22px' }}>{bajada}</p>}
    </>
  )
}

// ══════════════ 1 · PEDIR EL ENLACE ═══════════════════════════════════════════════════════════
export function OlvideContrasena() {
  const [email, setEmail] = useState('')
  const [estado, setEstado] = useState(null)   // { tipo, texto }
  const [cargando, setCargando] = useState(false)

  const enviar = async (e) => {
    e.preventDefault()
    setCargando(true); setEstado(null)
    try {
      const { data } = await axios.post(`${API}/olvide`, { email })
      setEstado({ tipo: 'ok', texto: data.mensaje })
    } catch (err) {
      setEstado({ tipo: 'error', texto: err?.response?.data?.error || t('pages.RestablecerPassword.no_se_ha_podido_enviar', 'No se ha podido enviar. Inténtalo otra vez.') })
    } finally { setCargando(false) }
  }

  return (
    <div style={marco}>
      <div style={tarjeta}>
        <Cabecera titulo={t('pages.RestablecerPassword.has_olvidado_tu_contrasena', '¿Has olvidado tu contraseña?')}
          bajada={t('pages.RestablecerPassword.escribe_tu_correo_y_te', 'Escribe tu correo y te mandamos un enlace para elegir una nueva.')} />
        {estado && <Aviso tipo={estado.tipo}>{estado.texto}</Aviso>}
        {estado?.tipo !== 'ok' && (
          <form onSubmit={enviar}>
            <div style={{ marginBottom: '18px' }}>
              <label style={label} htmlFor="email">{t('pages.RestablecerPassword.tu_correo', 'Tu correo')}</label>
              <input style={input} id="email" name="email" type="email" required autoComplete="email"
                value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.es" autoFocus />
              <p style={ayuda}>{t('pages.RestablecerPassword.el_mismo_con_el_que', 'El mismo con el que entras en Biango.')}</p>
            </div>
            <Boton type="submit" variante="primario" disabled={cargando || !email} style={boton}>
              {cargando ? t('pages.RestablecerPassword.enviando', 'Enviando…') : t('pages.RestablecerPassword.enviarme_el_enlace', 'Enviarme el enlace')}
            </Boton>
          </form>
        )}
        <p style={{ ...ayuda, textAlign: 'center', marginTop: '20px' }}>
          <Link to="/login" style={{ color: B.enlace, fontWeight: 700, textDecoration: 'none' }}>{t('pages.RestablecerPassword.volver_a_la_pantalla_de', 'Volver a la pantalla de acceso')}</Link>
        </p>
      </div>
    </div>
  )
}

// ══════════════ 2 · CANJEAR EL ENLACE ═════════════════════════════════════════════════════════
export default function RestablecerPassword() {
  const { token } = useParams()
  const navigate = useNavigate()
  const [pide, setPide] = useState(null)       // lo que el servidor dice que hay que dar
  const [error, setError] = useState(null)
  const [hecho, setHecho] = useState(false)
  const [cargando, setCargando] = useState(false)
  const [f, setF] = useState({ codigo: '', telefono4: '', password: '', password2: '' })

  useEffect(() => {
    let vivo = true
    axios.get(`${API}/restablecer/${token}`)
      .then(({ data }) => { if (vivo) setPide(data) })
      .catch(err => { if (vivo) setError(err?.response?.data?.error || t('pages.RestablecerPassword.no_se_ha_podido_comprobar', 'No se ha podido comprobar el enlace.')) })
    return () => { vivo = false }
  }, [token])

  const cambiar = (k) => (e) => setF({ ...f, [k]: e.target.value })

  const enviar = async (e) => {
    e.preventDefault()
    if (f.password !== f.password2) return setError(t('pages.RestablecerPassword.las_dos_contrasenas_no_coinciden', 'Las dos contraseñas no coinciden.'))
    setCargando(true); setError(null)
    try {
      await axios.post(`${API}/restablecer`, { token, codigo: f.codigo, telefono4: f.telefono4, password: f.password })
      setHecho(true)
      setTimeout(() => navigate('/login'), 2600)
    } catch (err) {
      const d = err?.response?.data
      setError(d?.error || t('pages.RestablecerPassword.no_se_ha_podido_cambiar', 'No se ha podido cambiar la contraseña.'))
      // Bloqueado por intentos: el enlace ya no vale, así que se cierra el formulario.
      if (d?.bloqueado) setPide(null)
      else if (typeof d?.intentos_restantes === 'number') setPide(p => p ? { ...p, intentos_restantes: d.intentos_restantes } : p)
    } finally { setCargando(false) }
  }

  if (hecho) {
    return (
      <div style={marco}><div style={{ ...tarjeta, textAlign: 'center' }}>
        <Cabecera titulo={t('pages.RestablecerPassword.contrasena_cambiada', 'Contraseña cambiada')} />
        <Aviso tipo="ok">{t('pages.RestablecerPassword.ya_puedes_entrar_con_la', 'Ya puedes entrar con la contraseña nueva. Te llevamos a la pantalla de acceso…')}</Aviso>
      </div></div>
    )
  }

  if (!pide) {
    return (
      <div style={marco}><div style={tarjeta}>
        <Cabecera titulo={t('pages.RestablecerPassword.restablecer_la_contrasena', 'Restablecer la contraseña')} />
        {error
          ? <>
              <Aviso tipo="error">{error}</Aviso>
              <Link to="/olvide-mi-contrasena" style={{ ...boton, display: 'flex', alignItems: 'center', textDecoration: 'none' }}>{t('pages.RestablecerPassword.pedir_otro_enlace', 'Pedir otro enlace')}</Link>
            </>
          : <p style={ayuda}>{t('pages.RestablecerPassword.comprobando_el_enlace', 'Comprobando el enlace…')}</p>}
      </div></div>
    )
  }

  const porWhatsapp = pide.canal === 'whatsapp'
  return (
    <div style={marco}>
      <div style={tarjeta}>
        <Cabecera titulo={t('pages.RestablecerPassword.elige_una_contrasena_nueva', 'Elige una contraseña nueva')}
          bajada={porWhatsapp
            ? t('pages.RestablecerPassword.te_hemos_mandado_un_codigo', 'Te hemos mandado un código de 6 dígitos por WhatsApp al teléfono de tu ficha.')
            : t('pages.RestablecerPassword.el_codigo_de_6_digitos', 'El código de 6 dígitos está en el correo que te acabamos de enviar.')} />
        {error && <Aviso tipo="error">{error}</Aviso>}
        <form onSubmit={enviar}>
          <div style={{ marginBottom: '16px' }}>
            <label style={label} htmlFor="codigo">{t('pages.RestablecerPassword.codigo_de_6_digitos', 'Código de 6 dígitos')}</label>
            <input style={{ ...input, letterSpacing: '.4em', fontWeight: 700, fontSize: '18px' }} id="codigo" name="codigo"
              inputMode="numeric" autoComplete="one-time-code" maxLength={6} required autoFocus
              value={f.codigo} onChange={cambiar('codigo')} placeholder="······" />
            <p style={ayuda}>{porWhatsapp ? t('pages.RestablecerPassword.mira_tu_whatsapp', 'Mira tu WhatsApp.') : t('pages.RestablecerPassword.mira_el_correo_si_no', 'Mira el correo; si no está, revisa la carpeta de spam.')}</p>
          </div>

          {pide.pide_telefono && (
            <div style={{ marginBottom: '16px' }}>
              <label style={label} htmlFor="telefono4">{t('pages.RestablecerPassword.los_4_ultimos_digitos_de', 'Los 4 últimos dígitos de tu móvil')}</label>
              <input style={{ ...input, letterSpacing: '.3em', fontWeight: 700 }} id="telefono4" name="telefono4"
                inputMode="numeric" maxLength={4}
                value={f.telefono4} onChange={cambiar('telefono4')} placeholder="····" />
              <p style={ayuda}>{t('pages.RestablecerPassword.el_telefono_que_tienes_en', 'El teléfono que tienes en tu ficha de la clínica. Así, con el correo solo no basta. Si en tu ficha no hay ningún teléfono, déjalo en blanco.')}</p>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={label} htmlFor="password">{t('pages.RestablecerPassword.contrasena_nueva', 'Contraseña nueva')}</label>
            <InputPassword id="password" name="password" autoComplete="new-password" required
              value={f.password} onChange={cambiar('password')} placeholder="••••••••" style={input} />
            <p style={ayuda}>{tx('pages.RestablecerPassword.al_menos_caracteres', 'Al menos {{x}} caracteres.', { x: pide.min_password || 8 })}</p>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={label} htmlFor="password2">{t('pages.RestablecerPassword.repitela', 'Repítela')}</label>
            <InputPassword id="password2" name="password2" autoComplete="new-password" required
              value={f.password2} onChange={cambiar('password2')} placeholder="••••••••" style={input} />
          </div>

          <Boton type="submit" variante="primario" disabled={cargando} style={boton}>
            {cargando ? t('pages.RestablecerPassword.guardando', 'Guardando…') : t('pages.RestablecerPassword.cambiar_la_contrasena', 'Cambiar la contraseña')}
          </Boton>
        </form>

        <p style={{ ...ayuda, marginTop: '16px' }}>
          {tx('pages.RestablecerPassword.al_cambiarla_se_cerraran_las', 'Al cambiarla se cerrarán las sesiones abiertas en todos tus dispositivos.{{x}}', { x: typeof pide.intentos_restantes === 'number' && pide.intentos_restantes <= 2 &&
            t('pages.RestablecerPassword.te_quedan', { one: ' Te quedan {{intentos_restantes}} intento.', other: ' Te quedan {{intentos_restantes}} intentos.' }, { intentos_restantes: pide.intentos_restantes, count: pide.intentos_restantes, _forma: (pide.intentos_restantes === 1) ? 'one' : 'other' }) })}
        </p>
      </div>
    </div>
  )
}
